import React from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, Check, Minus } from "lucide-react";
import Seo from "../components/Seo";
import BreadcrumbSchema from "../components/BreadcrumbSchema";
import { Button } from "../components/ui/button";
import NotFoundPage from "./NotFoundPage";
import { getBlogPost } from "../data/blogPosts";
import { SITE_ORIGIN } from "../seo/openGraph";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" },
  }),
};

function InlineText({ text }) {
  const re = /\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)\s]+)\)/g;
  const nodes = [];
  let last = 0;
  const className = "text-[#00AEEF] underline-offset-4 hover:underline";
  for (const match of text.matchAll(re)) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    const label = match[1];
    const href = match[2];
    if (href.startsWith("/")) {
      nodes.push(
        <Link key={`${href}-${match.index}`} to={href} className={className}>
          {label}
        </Link>,
      );
    } else {
      nodes.push(
        <a
          key={`${href}-${match.index}`}
          href={href}
          className={className}
          target="_blank"
          rel="noopener noreferrer"
        >
          {label}
        </a>,
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function ProseSection({ section, index }) {
  return (
    <motion.section
      id={section.id}
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
      custom={index}
      className="mb-12"
    >
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-white mb-4">{section.heading}</h2>
      {section.paragraphs?.map((paragraph) => (
        <p key={paragraph} className="text-gray-300 leading-relaxed mb-4">
          <InlineText text={paragraph} />
        </p>
      ))}
      {section.bullets?.length > 0 && (
        <ul className="space-y-3 mb-4">
          {section.bullets.map((item) => (
            <li key={item} className="flex items-start gap-3 text-gray-300 leading-relaxed">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#00AEEF] flex-shrink-0" />
              <span>
              <InlineText text={item} />
            </span>
            </li>
          ))}
        </ul>
      )}
      {section.after?.map((paragraph) => (
        <p key={paragraph} className="text-gray-300 leading-relaxed mb-4">
          <InlineText text={paragraph} />
        </p>
      ))}
    </motion.section>
  );
}

function ScopeList({ title, items, tone, testId }) {
  const accent = tone === "keep" ? "#10B981" : "#F59E0B";
  const Icon = tone === "keep" ? Check : Minus;
  return (
    <div className="bg-[#121212] border border-white/5 rounded-xl p-6 md:p-8" data-testid={testId}>
      <h2 className="text-xl font-bold text-white mb-6">{title}</h2>
      <ul className="space-y-4">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-3">
            <span
              className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-md flex-shrink-0"
              style={{ backgroundColor: `${accent}22`, color: accent }}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
            </span>
            <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getBlogPost(slug);

  if (!post) return <NotFoundPage />;

  const url = `${SITE_ORIGIN}${post.path}`;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.published,
    dateModified: post.published,
    mainEntityOfPage: url,
    author: {
      "@type": "Organization",
      name: "Maxiom Apps",
      url: SITE_ORIGIN,
    },
    publisher: {
      "@type": "Organization",
      name: "Maxiom Apps",
      url: SITE_ORIGIN,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_ORIGIN}/maxiom-apps-logo.png`,
      },
    },
  };

  return (
    <>
      <Seo
        title={post.title}
        description={post.metaDescription}
        ogDescription={post.ogDescription}
        path={post.path}
        type="article"
        image={post.ogImage}
        imageAlt={post.imageAlt}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <BreadcrumbSchema
        items={[
          { name: "Home", url: SITE_ORIGIN },
          { name: "Blog", url: `${SITE_ORIGIN}/blog` },
          { name: post.title, url },
        ]}
      />

      <article className="bg-[#0A0A0A]">
        <header className="pt-32 pb-12">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
            <motion.div variants={fadeUp} initial="hidden" animate="visible">
              <Link
                to="/blog"
                className="text-sm text-gray-500 hover:text-white transition-colors"
              >
                Blog
              </Link>
              <p className="text-[#00AEEF] text-sm uppercase tracking-widest font-medium mt-6 mb-4">{post.eyebrow}</p>
              <h1
                className="text-4xl md:text-5xl font-bold tracking-tight mb-6"
                data-testid="blog-post-heading"
              >
                {post.title}
              </h1>
              <p className="text-sm text-gray-500 mb-6">Maxiom Apps · {post.publishedLabel}</p>
              <p className="text-lg text-neutral-300 leading-relaxed">{post.dek}</p>
            </motion.div>
          </div>
        </header>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl pb-8">
          {post.sections.map((section, index) => (
            <ProseSection key={section.id} section={section} index={index} />
          ))}
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl pb-8">
          {post.scopeIntro ? (
            <p className="text-gray-300 leading-relaxed mb-8 max-w-3xl">{post.scopeIntro}</p>
          ) : null}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ScopeList title={post.keepTitle} items={post.keep} tone="keep" testId="blog-keep" />
            <ScopeList title={post.cutTitle} items={post.cut} tone="cut" testId="blog-cut" />
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl pt-12 pb-4">
          {post.closing.map((section, index) => (
            <ProseSection key={section.id} section={section} index={index} />
          ))}
        </div>

        <section className="py-20 md:py-28">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">{post.cta.heading}</h2>
            <p className="text-gray-400 text-lg mb-10 leading-relaxed">{post.cta.body}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={post.cta.primary.to} data-testid="blog-cta-schedule">
                <Button className="bg-[#00AEEF] hover:bg-[#0095CC] text-white font-medium px-8 py-6 text-base rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(0,174,239,0.3)] hover:shadow-[0_0_30px_rgba(0,174,239,0.5)]">
                  {post.cta.primary.label} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to={post.cta.secondary.to} data-testid="blog-cta-assessment">
                <Button
                  variant="outline"
                  className="border-white/20 hover:border-[#00AEEF] text-white hover:text-[#00AEEF] px-8 py-6 text-base rounded-md transition-all duration-300"
                >
                  {post.cta.secondary.label} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}
